'use client'

import * as React from 'react'
import { Button } from '@repo/design-system-cf/components/button'
import { IconArrowOutward } from '@repo/design-system-cf/components/icons'
import { Link } from '@repo/web-ui/components/link'
import { LFS_URL } from '@repo/web-utils/env'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'

// Aug 1, 2026 at 23:59 CEST (UTC+2) = 21:59 UTC
const PROMO_END = new Date('2026-08-01T21:59:00Z')
const HAPPY_HORSE_URL = 'https://studio.creativefabrica.com/ai-video-generator?model=Happyhorse'

const LFS = (LFS_URL ?? '').replace(/\/$/, '')
const HORSE_ICON = `${LFS}/web/pages/homepage/experiments/happy-horse.webp`
const BG_2X = `${LFS}/web/pages/homepage/experiments/red-bg_1448x603.webp`

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
}

function calculateTimeLeft(): TimeLeft {
  const diff = Math.max(0, PROMO_END.getTime() - Date.now())
  return {
    days: Math.floor(diff / 86_400_000),
    hours: Math.floor((diff % 86_400_000) / 3_600_000),
    minutes: Math.floor((diff % 3_600_000) / 60_000),
    seconds: Math.floor((diff % 60_000) / 1000),
  }
}

const SSR_DEFAULT: TimeLeft = { days: 23, hours: 59, minutes: 59, seconds: 59 }

function CountdownUnit({
  value,
  label,
  showSeparator = false,
}: {
  value: number
  label: string
  showSeparator?: boolean
}) {
  return (
    <div className={tw('flex flex-col items-center gap-1', showSeparator && 'pr-2')}>
      <div
        className={tw(
          'relative min-w-[48px] rounded-md bg-cf-white p-1 text-center text-base font-bold tabular-nums text-cf-pink-600',
          showSeparator &&
            'after:absolute after:left-full after:top-1/2 after:-translate-y-1/2 after:pl-1 after:font-bold after:text-cf-white after:content-[":"]',
        )}
      >
        {String(value).padStart(2, '0')}
      </div>
      <span className="text-[10px] text-cf-white/80">{label}</span>
    </div>
  )
}

export function HappyHorsePromoCard() {
  const t = useTranslations('pages.home.image_tools_strip.happy_horse_promo')

  const [timeLeft, setTimeLeft] = React.useState<TimeLeft>(SSR_DEFAULT)
  const [hasMounted, setHasMounted] = React.useState(false)

  React.useEffect(() => {
    setHasMounted(true)
    setTimeLeft(calculateTimeLeft())
    const id = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000)
    return () => clearInterval(id)
  }, [])

  const { days, hours, minutes, seconds } = hasMounted ? timeLeft : SSR_DEFAULT

  return (
    <div className="relative flex h-full min-h-[220px] flex-col justify-between overflow-hidden rounded-lg p-6 lg:p-8">
      {/* Background image — unoptimized because LFS domain is not in next/image config */}
      <Image src={BG_2X} alt="" fill unoptimized className="object-cover" />

      {/* Content sits above the background */}
      <div className="relative flex flex-col justify-between gap-2 h-full">
        {/* Horse icon + title + discount */}
        <div className="flex flex-col gap-2 text-cf-white">
          <Image src={HORSE_ICON} alt="" width={48} height={48} unoptimized className="size-12" />
          <h2 className="text-xl font-bold leading-none lg:text-2xl">{t('title')}</h2>
          <p className="text-lg">
            {t('discount_prefix')}{' '}
            <span className="font-bold text-cf-yellow-400">{t('discount')}</span>
          </p>
        </div>

        {/* Countdown */}
        <div className="flex flex-col gap-2 mb-2">
          <p className="text-xs font-bold text-cf-white">{t('countdown_label')}</p>
          <div className="flex items-end gap-1.5">
            <CountdownUnit value={days} label={t('countdown_days')} showSeparator />
            <CountdownUnit value={hours} label={t('countdown_hours')} showSeparator />
            <CountdownUnit value={minutes} label={t('countdown_minutes')} showSeparator />
            <CountdownUnit value={seconds} label={t('countdown_seconds')} />
          </div>
        </div>

        <Button
          variant="primary-inverse"
          size="small"
          asChild
          className="self-start mt-auto flex gap-1 text-cf-pink-600"
        >
          <Link href={HAPPY_HORSE_URL} target="_blank" rel="noopener noreferrer">
            <span>{t('button')}</span>
            <IconArrowOutward />
          </Link>
        </Button>
      </div>
    </div>
  )
}
